import interactionParserUtils from './InteractionParserUtils';
import { parseImage } from '../MediaModels';
import utilService from '../../../../services/UtilService';

const dragAndDropParser = {
    getGapTextModels(rootElement) {
        const gapTexts = Array.from(rootElement.getElementsByTagName('gapText'));
        return gapTexts.map((gapTextElement) => {
            const { identifier, matchMax, matchMin } = interactionParserUtils.getNonLanguageAttributes(gapTextElement);
            return {
                identifier,
                isPositioned: false,
                matchMax: utilService.getIntOr(matchMax),
                matchMin: utilService.getIntOr(matchMin),
                textContent: gapTextElement.textContent,
                type: 'gapText',
            };
        });
    },

    /**
     * We need to look for the Inspera custom x/y props AND the QTI standard top/left.
     *
     * @param interactionNode
     * @param language
     * @param defaultLanguage
     * @param assessmentRunId
     * @param questionIdentifier
     * @param mediaInfo
     * @return {*|Array}
     */
    getGapImgModels(interactionNode, language, defaultLanguage, assessmentRunId, questionIdentifier, mediaInfo) {
        const gapImages = interactionNode.getElementsByTagName('gapImg');
        return Array.from(gapImages).map((gapImgElement) => {
            let mediaModel;
            const { identifier, matchMax, matchMin } = interactionParserUtils.getNonLanguageAttributes(gapImgElement);
            const objectElement = gapImgElement.getElementsByTagName('object')[0];
            const objectModel = interactionParserUtils.parseListOfChildren(
                gapImgElement,
                'object',
                language,
                defaultLanguage
            )[0];

            const textDiv = gapImgElement.getElementsByTagName('div');
            const textContent = textDiv.length > 0 && textDiv[0].getAttribute('class') === 'text' ?
                interactionParserUtils.getNodeContent(textDiv[0], language, defaultLanguage)
                : objectModel.content;

            const width = parseInt(objectModel.width, 10);
            const height = parseInt(objectModel.height, 10);
            let top = parseInt(objectModel.top, 10);
            let left = parseInt(objectModel.left, 10);

            if (isNaN(left)) {
                const x = parseInt(objectModel.x, 10);
                left = utilService.getIntOr(x);
            }
            if (isNaN(top)) {
                const y = parseInt(objectModel.y, 10);
                top = utilService.getIntOr(y);
            }

            const geography = {
                width: utilService.getIntOr(width),
                height: utilService.getIntOr(height),
                top,
                left,
            };

            // When we are dealing with an Inspera authored interaction
            if (objectModel['inspera:logicalName'] !== undefined) {
                mediaModel = parseImage(questionIdentifier, objectElement, assessmentRunId, mediaInfo);
            }

            const isPositioned = geography.top !== undefined;
            return { geography, identifier, isPositioned, matchMax, matchMin, mediaModel, textContent, type: 'gapImg' };
        });
    },

    getBackgroundImageModel(interactionNode, assessmentRunId, questionIdentifier, legacyHeightAttribute) {
        const objectElements = interactionNode.getElementsByTagName('object');
        if (objectElements.length === 0) {
            return undefined;
        }

        const backgroundObject = objectElements[0];
        const parentNode = backgroundObject.parentNode;
        if (parentNode.localName !== 'graphicGapMatchInteraction') {
            return undefined;
        }

        const imageModel = parseImage(questionIdentifier, objectElements[0], assessmentRunId);
        const parsedLegacyHeight = parseInt(legacyHeightAttribute, 10);
        if ((!imageModel.height || isNaN(imageModel.height)) && !isNaN(parsedLegacyHeight)) {
            imageModel.height = parsedLegacyHeight;
        }
        if (!imageModel.width || isNaN(imageModel.width)) {
            imageModel.width = 620;
        }

        return imageModel;
    },

    parse(interactionNode, language, defaultLanguage, responseDeclarations, assessmentRunId, questionIdentifier, mediaInfo) {
        const useQTIv2p1 = interactionNode.getAttribute('inspera:useQTIv2p1') === 'true';
        const canvasHeight = parseInt(interactionNode.getAttribute('inspera:canvasHeight'), 10);
        const classNames = interactionNode.getAttribute('class') || '';
        const gapTextModels = this.getGapTextModels(interactionNode);
        const gapImgModels = this.getGapImgModels(
            interactionNode,
            language,
            defaultLanguage,
            assessmentRunId,
            questionIdentifier,
            mediaInfo,
        );

        const gapChoiceModels = gapImgModels.concat(gapTextModels);
        const hasUnPositionedGapChoices = gapChoiceModels.some(model => model.isPositioned === false);
        const extraResponseIdentifiers = interactionParserUtils.getIdentifiersFromElements(gapChoiceModels);
        const objects = interactionParserUtils.parseListOfChildren(interactionNode, 'object', language, defaultLanguage);
        const associableHotspots = interactionParserUtils.parseListOfChildren(
            interactionNode,
            'associableHotspot',
            language,
            defaultLanguage
        );

        const freePlacing = classNames.includes('freeplacing');
        const markHotspots = classNames.includes('markHotspots');
        const backgroundImageModel = this.getBackgroundImageModel(
            interactionNode,
            assessmentRunId,
            questionIdentifier,
            canvasHeight
        );
        const prompt = interactionParserUtils.getPrompt(interactionNode, language, defaultLanguage);

        const extraResponseDeclarations = responseDeclarations.filter((declaration) => {
            return extraResponseIdentifiers.includes(declaration.identifier);
        });

        const regardUnansweredOutsideTarget = extraResponseDeclarations.length === 0;
        if (regardUnansweredOutsideTarget) {
            extraResponseIdentifiers.forEach((identifier) => {
                extraResponseDeclarations.push({
                    baseType: 'point',
                    cardinality: 'single',
                    identifier,
                });
            });
        }

        for (const object of objects) {
            object.data = interactionParserUtils.getMediaFileUrl(object, assessmentRunId);
        }

        return {
            useQTIv2p1,
            backgroundImageModel,
            gapChoiceModels,
            associableHotspots,
            extraResponseDeclarations,
            freePlacing,
            hasUnPositionedGapChoices,
            markHotspots,
            objects,
            prompt,
            regardUnansweredOutsideTarget,
            canvasHeight: utilService.getIntOr(canvasHeight),
        };
    },
};

export default dragAndDropParser;

